import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { leadService, usersService, settingsService, tagService, customFieldService } from '../services/api';
import { useInvoice } from '../context/InvoiceContext';
import Select from '../components/Select';
import SearchInput from '../components/SearchInput';
import ChangeHistory from '../components/ChangeHistory';
import Footer from '../components/Footer';

// Module-level cache: persists across route changes, cleared on logout/refresh
let _leadsCache = null;

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const ordinal = (n) => { const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
const fmtDate = (val) => {
  if (!val) return '-';
  const d = val instanceof Date ? val : new Date(val.length === 10 ? val + 'T00:00:00' : val);
  if (isNaN(d)) return '-';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${ordinal(d.getDate())} ${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'Not Contacted', color: '#6366F1', bg: '#EEF2FF' },
  { value: 'contacted', label: 'First call - Done', color: '#0891B2', bg: '#ECFEFF' },
{ value: 'shows_interest', label: 'Shows Interest', color: '#7C3AED', bg: '#F5F3FF' },
  { value: 'high_interest', label: 'High Interest', color: '#C2410C', bg: '#FFF7ED' },
  { value: 'call_not_picking', label: 'Call Not Picking', color: '#B45309', bg: '#FEF3C7' },
  { value: 'ready', label: 'Ready', color: '#16A34A', bg: '#DCFCE7' },
  { value: 'followup_later', label: 'Follow-up Later', color: '#0369A1', bg: '#E0F2FE' },
  { value: 'coimbatore', label: 'Coimbatore', color: '#9A3412', bg: '#FFEDD5' },
  { value: 'won', label: 'Won', color: '#059669', bg: '#ECFDF5' },
  { value: 'lost', label: 'Lost', color: '#DC2626', bg: '#FEF2F2' },
  { value: 'not_interested', label: 'Not Interested', color: '#6B7280', bg: '#F3F4F6' },
  { value: 'closed', label: 'Closed', color: '#374151', bg: '#F9FAFB' },
  { value: 'no_response', label: 'No Response', color: '#92400E', bg: '#FFFBEB' },
];

const TYPE_OPTIONS = [
  { value: '', label: '—', color: '#6B7280', bg: 'transparent' },
  { value: 'paying', label: 'Paying', color: '#065F46', bg: '#D1FAE5' },
  { value: 'free_scheme', label: 'Free Scheme', color: '#1E40AF', bg: '#DBEAFE' },
];

const MODE_OPTIONS = [
  { value: '', label: '—', color: '#6B7280', bg: 'transparent' },
  { value: 'online', label: 'Online', color: '#0369A1', bg: '#E0F2FE' },
  { value: 'office', label: 'Office', color: '#9A3412', bg: '#FFEDD5' },
];

// Path options are shared with Customers (persisted via settingsService 'customer_paths') — see pathOptions state below

// Field options are shared with Customers (persisted via settingsService 'customer_fields') — see fieldOptions state below

const getOpt = (options, value) => options.find(o => o.value === value) || options[0];
const getStatusStyle = (status) => { const s = getOpt(STATUS_OPTIONS, status); return { color: s.color, background: s.bg, padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }; };

// Strips spaces/punctuation and any country code so two numbers count as the
// same phone regardless of formatting — compare by the trailing 10 digits
// (a plain 10-digit Indian number, or the local part of any longer number
// with a country code in front).
const normalizePhoneForDup = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length > 10 ? digits.slice(-10) : digits;
};

function findPhoneDuplicateGroups(leads) {
  const byKey = new Map();
  leads.forEach(l => {
    const key = normalizePhoneForDup(l.phone);
    if (!key || key.length < 7) return; // skip blank/too-short numbers — not a meaningful match
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(l);
  });
  return [...byKey.values()]
    .filter(group => group.length > 1)
    .sort((a, b) => b.length - a.length);
}

const InlineBadgeSelect = ({ value, options, onSave }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (saving) return;
    if (open) { setOpen(false); return; }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
    }
    setOpen(true);
  };

  const handleSelect = async (val, e) => {
    e.stopPropagation();
    const newVal = val === value ? '' : val;
    setSaving(true);
    setOpen(false);
    try { await onSave(newVal); }
    finally { setSaving(false); }
  };

  const opt = getOpt(options, value);
  const hasBadge = opt.value !== '';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <span
        ref={triggerRef}
        onClick={handleOpen}
        style={{
          padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '2px', letterSpacing: '0.04em', whiteSpace: 'nowrap',
          background: hasBadge ? opt.bg : 'transparent', color: hasBadge ? opt.color : '#9CA3AF',
          cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, userSelect: 'none',
        }}
      >
        {saving ? '…' : opt.label}
      </span>
      {open && pos && ReactDOM.createPortal(
        <div ref={ref} style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 10000, minWidth: '130px', overflow: 'hidden' }}>
          {options.map(o => (
            <button key={o.value} onClick={(e) => handleSelect(o.value, e)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: '12px', fontWeight: o.value === value ? 700 : 400, background: o.value === value && o.bg !== 'transparent' ? o.bg : 'transparent', color: o.value !== '' ? o.color : '#6B7280', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => { if (o.value !== value) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = o.value === value && o.bg !== 'transparent' ? o.bg : 'transparent'; }}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Voice Recorder ────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';

const VoiceRecorder = ({ leadId }) => {
  const { currentUser } = useInvoice();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [recording, setRecording] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [saved, setSaved] = useState([]);
  const savedRef = useRef([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [pendingBlob, setPendingBlob] = useState(null);
  const [pendingNotes, setPendingNotes] = useState('');
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  useEffect(() => {
    if (!leadId) return;
    leadService.getVoiceNotes(leadId).then(res => { const d = res.data || []; setSaved(d); savedRef.current = d; }).catch(() => {});
  }, [leadId]);

  const start = async () => {
    setError('');
    setCountdown(5);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const options = { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 32000 };
      mediaRecorder.current = new MediaRecorder(stream, options);
      chunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: mediaRecorder.current.mimeType });
        stream.getTracks().forEach((t) => t.stop());
        setPendingBlob(blob);
        setPendingNotes('');
      };
      for (let i = 4; i >= 1; i--) {
        await new Promise(r => setTimeout(r, 1000));
        setCountdown(i);
      }
      await new Promise(r => setTimeout(r, 1000));
      mediaRecorder.current.start(100);
      setCountdown(0);
      setRecording(true);
    } catch (err) {
      setCountdown(0);
      setError(err.name === 'NotAllowedError' ? 'Microphone access denied. Please allow microphone permission.' : err.message);
    }
  };

  const stop = () => {
    if (mediaRecorder.current?.state === 'recording') {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  };


  const savePending = async () => {
    if (!pendingNotes.trim() || !pendingBlob) return;
    const total = savedRef.current.length + 1;
    const ordinal = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'][total - 1] || `#${total}`;
    const title = `${ordinal} recording`;
    setSaving(true);
    try {
      await leadService.uploadVoiceNote(leadId, pendingBlob, title, pendingNotes.trim());
      const fresh = await leadService.getVoiceNotes(leadId);
      const d = fresh.data || [];
      setSaved(d);
      savedRef.current = d;
      setPendingBlob(null);
      setPendingNotes('');
    } catch (err) {
      setError('Failed to save: ' + (err.message || 'Unknown error'));
    }
    setSaving(false);
  };

  const saveNote = async (noteId) => {
    try {
      const res = await leadService.updateVoiceNote(leadId, noteId, noteText);
      setSaved(prev => { const next = prev.map(n => n.id === noteId ? res.data : n); savedRef.current = next; return next; });
      setEditingNoteId(null);
    } catch (err) {
      setError('Failed to save note: ' + (err.message || 'Unknown error'));
    }
  };

  const deleteNote = async (id) => {
    try {
      await leadService.deleteVoiceNote(leadId, id);
      setSaved((prev) => prev.filter(n => n.id !== id));
    } catch (err) {
      setError('Failed to delete: ' + (err.message || 'Unknown error'));
    }
  };

  return (
    <div style={{ marginTop: 'var(--spacing-md)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--spacing-md)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Voice Notes</div>
        {!recording && !countdown && !pendingBlob ? (
          <button className="btn btn-primary btn-sm" onClick={start} style={{ fontSize: '11px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} /> Record
          </button>
        ) : countdown > 0 ? (
          <span style={{ fontSize: '14px', color: '#F59E0B', fontWeight: 700, fontFamily: 'monospace' }}>{countdown}</span>
        ) : (
          <button className="btn btn-secondary btn-sm" onClick={stop} style={{ fontSize: '11px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4, color: '#DC2626' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: '#DC2626', display: 'inline-block' }} /> Stop
          </button>
        )}
      </div>
      {recording && <div style={{ fontSize: '11px', color: '#EF4444', fontWeight: 600, marginBottom: 8 }}>Recording...</div>}
      {saving && <div style={{ fontSize: '11px', color: '#F59E0B', fontWeight: 600, marginBottom: 8 }}>Saving...</div>}
      {error && <div style={{ color: '#DC2626', fontSize: '11px', marginBottom: 8 }}>{error}</div>}

      {/* Pending recording — notes required before saving */}
      {pendingBlob && (
        <div style={{ marginBottom: 8, background: '#FEF3C7', borderRadius: 4, padding: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: 4 }}>Add notes for this recording *</div>
          <textarea value={pendingNotes} onChange={e => setPendingNotes(e.target.value)} placeholder="Describe what this recording is about..." rows={2} style={{ width: '100%', fontSize: '11px', padding: '4px 6px', border: '1px solid var(--color-gray-300)', borderRadius: 3, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} autoFocus />
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <button onClick={savePending} disabled={!pendingNotes.trim() || saving} className="btn btn-primary btn-sm" style={{ fontSize: '10px', padding: '3px 10px', opacity: !pendingNotes.trim() ? 0.5 : 1 }}>{saving ? 'Saving...' : 'Save'}</button>
            {isSuperAdmin && <button onClick={() => { setPendingBlob(null); setPendingNotes(''); }} className="btn btn-secondary btn-sm" style={{ fontSize: '10px', padding: '3px 10px' }}>Discard</button>}
          </div>
        </div>
      )}

      {/* Saved recordings */}
      {saved.map((n) => (
        <div key={n.id} style={{ marginBottom: 6, background: 'var(--color-gray-50)', borderRadius: 4, padding: '6px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
              <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)' }}>
                {n.creator?.loginId && cap(n.creator.loginId)} · {fmtDate(n.createdAt)} {new Date(n.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </div>
            </div>
            {isSuperAdmin && <button onClick={() => deleteNote(n.id)} style={{ fontSize: '10px', color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Delete</button>}
          </div>
          <audio controls preload="auto" src={`${API_BASE}/uploads/${n.filename}`} style={{ width: '100%', height: 32 }} />
          {editingNoteId === n.id ? (
            <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." rows={3} style={{ flex: 1, fontSize: '11px', padding: '3px 6px', border: '1px solid var(--color-gray-300)', borderRadius: 3, resize: 'vertical', fontFamily: 'inherit' }} autoFocus onKeyDown={e => { if (e.key === 'Escape') setEditingNoteId(null); }} />
              <button onClick={() => saveNote(n.id)} style={{ fontSize: '10px', padding: '2px 8px', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer' }}>Save</button>
              <button onClick={() => setEditingNoteId(null)} style={{ fontSize: '10px', padding: '2px 8px', background: 'var(--color-gray-200)', border: 'none', borderRadius: 3, cursor: 'pointer' }}>Cancel</button>
            </div>
          ) : (
            <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              {n.notes ? (
                <span onClick={() => { setEditingNoteId(n.id); setNoteText(n.notes || ''); }} style={{ fontSize: '11px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{n.notes}</span>
              ) : (
                <span onClick={() => { setEditingNoteId(n.id); setNoteText(''); }} style={{ fontSize: '10px', color: 'var(--color-gray-400)', cursor: 'pointer' }}>+ Add note</span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: '14px' }}>{children}</div>
  </div>
);

const LeadDetailModal = ({ lead, onClose, onStatusSave, onEdit }) => {
  const [status, setStatus] = useState(lead.status || 'new');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const changed = status !== lead.status;

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    leadService.history(lead.id).then(res => setHistory(res.data || [])).catch(() => {}).finally(() => setHistoryLoading(false));
  }, [lead.id]);

  const handleSave = async () => {
    setSaving(true);
    try { await onStatusSave(lead.id, status); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex-between mb-lg">
          <h3>{lead.name}</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: 'var(--spacing-lg)' }}>
          <Field label="WhatsApp">{lead.phone || '—'}</Field>
          <Field label="Status"><span style={getStatusStyle(lead.status)}>{lead.status}</span></Field>
          {lead.cardStatus && <Field label="Card"><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px', background: lead.cardStatus === 'yellow' ? '#FEF08A' : '#FCA5A5', color: lead.cardStatus === 'yellow' ? '#92400E' : '#991B1B' }}>{lead.cardStatus === 'yellow' ? '🟨 Suspicious' : '🟥 Blacklisted'}</span></Field>}
          {lead.path && <Field label="Path"><span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{lead.path}</span></Field>}
          {lead.field && <Field label="Field"><span style={{ background: '#DBEAFE', color: '#1E40AF', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{lead.field}</span></Field>}
{lead.location && <Field label="Location"><span style={{ fontSize: '12px' }}>{lead.location}</span></Field>}
          {lead.instagram && <Field label="Instagram"><span style={{ fontSize: '12px' }}>{lead.instagram}</span></Field>}
          {lead.customerType && <Field label="Type">
            {lead.customerType === 'paying'
              ? <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Paying</span>
              : <span style={{ background: '#DBEAFE', color: '#1E40AF', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Free Scheme</span>}
          </Field>}
          {lead.mode && <Field label="Mode">
            {lead.mode === 'online'
              ? <span style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Online</span>
              : <span style={{ background: '#FFEDD5', color: '#9A3412', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Office</span>}
          </Field>}
          {lead.followUpDate && (
            <Field label="Follow-up">
              <span style={{ fontSize: '12px' }}>
                {fmtDate(lead.followUpDate)}
                {lead.followUpTime && (() => { const [h, m] = lead.followUpTime.split(':'); const hr = +h; return ` ${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; })()}
              </span>
            </Field>
          )}
          {(lead.tags || []).length > 0 && (
            <Field label="Tags">
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {(lead.tags || []).map(t => (
                  <span key={t.id} style={{ padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: t.color, color: '#fff' }}>{t.name}</span>
                ))}
              </div>
            </Field>
          )}
          {lead.notes && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span>Notes</span>
                {lead.notesUpdatedAt && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '11px' }}>{fmtDate(lead.notesUpdatedAt)}</span>}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{lead.notes}</div>
            </div>
          )}
        </div>

        <VoiceRecorder leadId={lead.id} />

        <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
          <label className="form-label">Change Status</label>
          <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <button type="button" onClick={() => setShowHistory(!showHistory)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: 'var(--color-primary)', padding: 0 }}>
            {showHistory ? '▾ Hide History' : '▸ Show History'} ({history.length})
          </button>
          {showHistory && (
            <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
              <ChangeHistory history={history} loading={historyLoading} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
          <button type="button" onClick={() => { onClose(); onEdit(lead); }} className="btn btn-secondary">Edit</button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">Close</button>
            {changed && (
              <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving…' : 'Save Change'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Leads = () => {
  const { currentUser, showNotification } = useInvoice();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [leads, setLeads] = useState(_leadsCache || []);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
const [filterPath, setFilterPath] = useState('');
  const [filterField, setFilterField] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [filterCreator, setFilterCreator] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('created_newest');
  const [detailLead, setDetailLead] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [filterTag, setFilterTag] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366F1');
  const [editingTag, setEditingTag] = useState(null); // { id, name, color }
  const [form, setForm] = useState({ name: '', phone: '', email: '', dateOfBirth: '', status: 'new', notes: '', location: '', instagram: '@', path: '', field: '', customerType: '', mode: '', followUpDate: '', followUpTime: '', followUpDone: false, cardStatus: '', attemptCount: null, tagIds: [], leadFrom: '', escalatedTo: '' });
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [calendarDay, setCalendarDay] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const savePrefsTimer = useRef(null);
  const [visibleCols, setVisibleCols] = useState(null);
  const [showColPicker, setShowColPicker] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null); // { id, date, time }
  const [customFields, setCustomFields] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [addingField, setAddingField] = useState(false);
  const [editingCustomCell, setEditingCustomCell] = useState(null); // { leadId, key, value }
  const [fieldOptions, setFieldOptions] = useState(['Nursing', 'Other']); // shared with Customers' "Field" dropdown
  const [managingFieldOptions, setManagingFieldOptions] = useState(false);
  const [newFieldOptionName, setNewFieldOptionName] = useState('');
  const [pathOptions, setPathOptions] = useState(['Ausbildung', 'Work visa', 'Bachelors', 'Masters']); // shared with Customers' "Path" dropdown
  const [managingPathOptions, setManagingPathOptions] = useState(false);
  const [newPathOptionName, setNewPathOptionName] = useState('');

  const loadLeads = useCallback(async () => {
    try {
      const res = await leadService.list();
      _leadsCache = res.data || [];
      setLeads(_leadsCache);
    } catch {}
  }, []);

  // Debounced save to API
  const savePrefsToApi = useCallback((cols) => {
    if (savePrefsTimer.current) clearTimeout(savePrefsTimer.current);
    savePrefsTimer.current = setTimeout(() => {
      usersService.savePreferences({ leads_columns: cols }).catch(() => {});
    }, 500);
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => {
    usersService.getPreferences().then(res => {
      const prefs = res.preferences || {};
      if (prefs.leads_columns) setVisibleCols(prefs.leads_columns);
    }).catch(() => {});
    tagService.list().then(res => setAllTags(res.data || [])).catch(() => {});
    customFieldService.list().then(res => setCustomFields(res.data || [])).catch(() => {});
    settingsService.get('customer_fields').then(val => {
      if (val) {
        try { setFieldOptions(JSON.parse(val)); } catch {}
      }
    }).catch(() => {});
    settingsService.get('customer_paths').then(val => {
      if (val) {
        try { setPathOptions(JSON.parse(val)); } catch {}
      }
    }).catch(() => {});
  }, []);

  const saveFieldOptions = async (opts) => {
    setFieldOptions(opts);
    try { await settingsService.set('customer_fields', JSON.stringify(opts)); } catch {}
  };

  const handleAddFieldOption = async () => {
    const name = newFieldOptionName.trim();
    if (!name || fieldOptions.includes(name)) return;
    await saveFieldOptions([...fieldOptions, name]);
    setNewFieldOptionName('');
  };

  const handleRemoveFieldOption = async (name) => {
    await saveFieldOptions(fieldOptions.filter(f => f !== name));
  };

  const savePathOptions = async (opts) => {
    setPathOptions(opts);
    try { await settingsService.set('customer_paths', JSON.stringify(opts)); } catch {}
  };

  const handleAddPathOption = async () => {
    const name = newPathOptionName.trim();
    if (!name || pathOptions.includes(name)) return;
    await savePathOptions([...pathOptions, name]);
    setNewPathOptionName('');
  };

  const handleRemovePathOption = async (name) => {
    await savePathOptions(pathOptions.filter(p => p !== name));
  };

  const resetForm = () => {
    setForm({ name: '', phone: '', email: '', dateOfBirth: '', status: 'new', notes: '', location: '', instagram: '@', path: '', field: '', customerType: '', mode: '', followUpDate: '', followUpTime: '', followUpDone: false, cardStatus: '', attemptCount: null, tagIds: [], leadFrom: '', escalatedTo: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const getAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const phone = form.phone?.trim() || null;
      const payload = { ...form, phone };
      if (editingId) {
        await leadService.update(editingId, payload);
      } else {
        await leadService.create(payload);
      }
      await loadLeads();
      resetForm();
    } catch (e) { showNotification(e.message || 'Failed to save lead', 'error'); }
    finally { setSaving(false); }
  };

  const startEdit = (l) => {
    setForm({ name: l.name, phone: l.phone || '+91 ', email: l.email || '', dateOfBirth: l.dateOfBirth || '', status: l.status || 'new', notes: l.notes || '', location: l.location || '', instagram: l.instagram || '@', path: l.path || '', field: l.field || '', customerType: l.customerType || '', mode: l.mode || '', followUpDate: l.followUpDate || '', followUpTime: l.followUpTime || '', followUpDone: l.followUpDone || false, cardStatus: l.cardStatus || '', attemptCount: l.attemptCount || null, tagIds: (l.tags || []).map(t => t.id), leadFrom: l.leadFrom || '', escalatedTo: l.escalatedTo || '' });
    setEditingId(l.id);
    setShowForm(true);
  };

  const navigate = useNavigate();

  const handleGenerateInvoice = (l) => {
    navigate('/generate-invoice', { state: { lead: { id: l.id, name: l.name, phone: l.phone, email: l.email, company: l.company, path: l.path, field: l.field, customerType: l.customerType } } });
  };

  const handleUnconvert = async (l) => {
    if (!l.customerId) return;
    try {
      await leadService.unconvert(l.id);
      await loadLeads();
    } catch (e) { showNotification(e.message || 'Failed to unconvert lead', 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await leadService.delete(id);
      await loadLeads();
    } catch (e) { showNotification(e.message || 'Failed to delete lead', 'error'); }
  };

  const parseTime12 = (t) => {
    if (!t) return { h: '12', m: '00', ampm: 'AM' };
    const [hh, mm] = t.split(':');
    const h = parseInt(hh, 10);
    return { h: String(h === 0 ? 12 : h > 12 ? h - 12 : h), m: mm || '00', ampm: h < 12 ? 'AM' : 'PM' };
  };
  const toTime24 = (h, m, ampm) => {
    let hh = parseInt(h, 10);
    if (ampm === 'AM' && hh === 12) hh = 0;
    if (ampm === 'PM' && hh !== 12) hh += 12;
    return `${String(hh).padStart(2, '0')}:${m}`;
  };

  const handleFieldSave = async (id, field, value) => {
    const payload = { [field]: value || null };
    try {
      await leadService.update(id, payload);
      await loadLeads();
    } catch (e) { showNotification(e.message || 'Failed to update lead', 'error'); }
  };

  const saveCustomCell = async (leadId, key, value) => {
    setEditingCustomCell(null);
    try {
      await leadService.update(leadId, { customData: { [key]: value } });
      await loadLeads();
    } catch (e) { showNotification(e.message || 'Failed to update', 'error'); }
  };

  const handleAddCustomField = async () => {
    if (!newFieldName.trim()) return;
    setAddingField(true);
    try {
      const res = await customFieldService.create(newFieldName.trim());
      setCustomFields(f => [...f, res.data]);
      setVisibleCols(c => [...(c || ALL_COLUMNS.filter(col => col.default).map(col => col.key)), res.data.key]);
      setNewFieldName('');
    } catch (e) { showNotification(e.message || 'Failed to add field', 'error'); }
    finally { setAddingField(false); }
  };

  const handleDeleteCustomField = async (fieldId, fieldKey) => {
    try {
      await customFieldService.delete(fieldId);
      setCustomFields(f => f.filter(cf => cf.id !== fieldId));
      setVisibleCols(c => (c || []).filter(k => k !== fieldKey));
    } catch (e) { showNotification(e.message || 'Failed to delete field', 'error'); }
  };

  const saveFollowUp = async (id, date, time) => {
    setEditingFollowUp(null);
    try {
      await leadService.update(id, { followUpDate: date || null, followUpTime: time || null });
      await loadLeads();
    } catch (e) { showNotification(e.message || 'Failed to update follow-up', 'error'); }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e, idx) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };
  const handleDragOver = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (idx !== draggedIdx) setDragOverIdx(idx); };
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === dropIdx) { setDraggedIdx(null); setDragOverIdx(null); return; }
    const reordered = [...filtered];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    // Optimistically update UI
    setLeads(prev => {
      const filteredIds = new Set(filtered.map(l => l.id));
      const nonFiltered = prev.filter(l => !filteredIds.has(l.id));
      return [...reordered, ...nonFiltered];
    });
    setDraggedIdx(null);
    setDragOverIdx(null);
    // Persist the full order (all leads, not just filtered)
    try {
      const allReordered = [...reordered, ...leads.filter(l => !filtered.some(f => f.id === l.id))];
      await leadService.reorder(allReordered.map(l => l.id));
    } catch (err) { console.error('Failed to save order:', err); await loadLeads(); }
  };

  // Derive unique filter options from data
const creatorOptions = [...new Map(leads.filter(l => l.creator).map(l => [l.creator.id, l.creator.loginId])).entries()].map(([id, loginId]) => ({ value: String(id), label: cap(loginId) }));

  // Scans every lead regardless of the current status filter/search, so a
  // duplicate hiding in a different status tab still surfaces here.
  const phoneDuplicateGroups = findPhoneDuplicateGroups(leads);

  const FIELD_OPTIONS = [{ value: '', label: '—', color: '#6B7280', bg: 'transparent' }, ...fieldOptions.map(f => ({ value: f, label: f, color: '#1E40AF', bg: '#DBEAFE' }))];
  const PATH_OPTIONS = [{ value: '', label: '—', color: '#6B7280', bg: 'transparent' }, ...pathOptions.map(p => ({ value: p, label: p, color: '#5B21B6', bg: '#EDE9FE' }))];

  const ALL_COLUMNS = [
    { key: 'name', label: 'Name', default: true },
    { key: 'phone', label: 'WhatsApp', default: true },
    { key: 'followUpDate', label: 'Follow-up', default: true },
    { key: 'createdBy', label: 'Created By', default: true },
    { key: 'createdAt', label: 'Created', default: true },
    { key: 'location', label: 'Location', default: true, width: '80px' },
    { key: 'status', label: 'Status', default: true },
    { key: 'age', label: 'Age', default: true },
    { key: 'customerType', label: 'Type', default: true },
    { key: 'mode', label: 'Mode', default: true },
    { key: 'path', label: 'Path', default: true },
    { key: 'field', label: 'Field', default: true },
    { key: 'notes', label: 'Notes', default: true },
    { key: 'updatedAt', label: 'Last Updated', default: false },
    { key: 'email', label: 'Email', default: false },
    { key: 'instagram', label: 'Instagram', default: false },
    { key: 'leadFrom', label: 'Lead From', default: false },
    { key: 'escalatedTo', label: 'Escalated To', default: false },
    ...customFields.map(f => ({ key: f.key, label: f.name, default: true, custom: true, customFieldId: f.id })),
  ];
  const cols = visibleCols || ALL_COLUMNS.filter(c => c.default).map(c => c.key);
  const toggleCol = (key) => {
    const next = cols.includes(key) ? cols.filter(k => k !== key) : [...cols, key];
    setVisibleCols(next);
    savePrefsToApi(next);
  };
  const isColVisible = (key) => cols.includes(key);

  const hasActiveFilters = search || filterPath || filterField || filterType || filterMode || filterCreator || filterTag || filterStatus;
  const clearFilters = () => { setSearch(''); setFilterPath(''); setFilterField(''); setFilterType(''); setFilterMode(''); setFilterCreator(''); setFilterTag(''); setFilterStatus(''); };

  const ARCHIVED_STATUSES = new Set(['lost', 'not_interested', 'won', 'closed', 'no_response', 'coimbatore']);
  const filtered = leads.filter(l => {
    if (filter === 'everything') {
      // no status filtering — show all leads
    } else if (filter === 'archived') {
      if (!ARCHIVED_STATUSES.has(l.status)) return false;
    } else if (filter === 'all') {
      if (ARCHIVED_STATUSES.has(l.status) || l.status === 'ready') return false;
    } else {
      if (l.status !== filter) return false;
    }
    if (filterStatus && l.status !== filterStatus) return false;
    if (filterTag && !(l.tags || []).some(t => t.id === Number(filterTag))) return false;
    if (filterPath && l.path !== filterPath) return false;
    if (filterField && l.field !== filterField) return false;
    if (filterType && l.customerType !== filterType) return false;
    if (filterMode && l.mode !== filterMode) return false;
    if (filterCreator && String(l.creator?.id) !== filterCreator) return false;
    if (search) {
      const s = search.toLowerCase();
      const normalizePhone = p => p ? p.replace(/[\s+\-().]/g, '') : '';
      const sDigits = search.replace(/[\s+\-().]/g, '');
      const phoneMatch = sDigits.length >= 3 && normalizePhone(l.phone).includes(sDigits);
      return (l.name?.toLowerCase().includes(s)) ||
        phoneMatch ||
        (l.notes?.toLowerCase().includes(s)) ||
        (l.creator?.loginId?.toLowerCase().includes(s));
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'created_newest') return new Date(b.createdAt) - new Date(a.createdAt);
    if (sortBy === 'created_oldest') return new Date(a.createdAt) - new Date(b.createdAt);
    if (sortBy === 'updated_newest') return new Date(b.updatedAt) - new Date(a.updatedAt);
    if (sortBy === 'updated_oldest') return new Date(a.updatedAt) - new Date(b.updatedAt);
    if (sortBy === 'followup_oldest' || sortBy === 'followup') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const aDate = a.followUpDate ? new Date(a.followUpDate) : null;
      const bDate = b.followUpDate ? new Date(b.followUpDate) : null;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      const rank = (l, d) => {
        if (!l.followUpDone && d >= today) return 0; // upcoming, not done
        if (l.followUpDone) return 1;                // done ✓
        return 2;                                     // overdue/red
      };
      const ra = rank(a, aDate), rb = rank(b, bDate);
      if (ra !== rb) return ra - rb;
      return aDate - bDate;
    }
    if (sortBy === 'followup_newest') {
      const aDate = a.followUpDate ? new Date(a.followUpDate) : null;
      const bDate = b.followUpDate ? new Date(b.followUpDate) : null;
      if (aDate && bDate) return bDate - aDate;
      if (bDate) return -1;
      if (aDate) return 1;
      return 0;
    }
    return 0;
  });

  const downloadCSV = () => {
    const escapeCell = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };
    const headers = [
      'ID', 'Follow-up Done', 'Attempt Count', 'Follow-up Time', 'Card Status',
      'Status', 'Customer Type', 'Mode', 'Path', 'Follow-up Date', 'Date of Birth',
      'Created By', 'Phone', 'Name', 'Field',
      'Customer', 'Tags', 'Location', 'Company', 'Instagram',
      'Created At', 'Updated At', 'Email', 'Lost Reason', 'Notes'
    ];
    const rows = filtered.map(l => [
      l.id,
      l.followUpDone ? 'Yes' : 'No',
      l.attemptCount ?? '',
      l.followUpTime || '',
      l.cardStatus || '',
      l.status || '',
      l.customerType || '',
      l.mode || '',
      l.path || '',
      l.followUpDate || '',
      l.dateOfBirth || '',
      l.creator?.loginId || '',
      l.phone || '',
      l.name,
      l.field || '',
      l.customer?.name || '',
      (l.tags || []).map(t => t.name).join('; '),
      l.location || '',
      l.company || '',
      l.instagram || '',
      l.createdAt ? new Date(l.createdAt).toISOString() : '',
      l.updatedAt ? new Date(l.updatedAt).toISOString() : '',
      l.email || '',
      l.lostReason || '',
      l.notes || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(escapeCell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="main-content">
        <div className="container" style={{ maxWidth: '100%' }}>
          <div className="flex-between mb-lg">
            <h1 style={{ display: 'inline-flex', alignItems: 'baseline', gap: '10px' }}>Leads <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-secondary)', background: 'var(--color-gray-100)', padding: '2px 10px', borderRadius: '12px', lineHeight: '1.4', position: 'relative', top: '-2px' }}>{leads.length}</span></h1>
            {!showForm && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowColPicker(!showColPicker)} className="btn btn-secondary">Columns</button>
                  {showColPicker && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowColPicker(false)} />
                      <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', color: '#1a1a1a', width: 'max-content' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '6px' }}>Show Columns</div>
                        {ALL_COLUMNS.map(c => (
                          <div key={c.key} style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#334155', flex: 1 }}>
                              <input type="checkbox" checked={isColVisible(c.key)} onChange={() => toggleCol(c.key)} style={{ accentColor: 'var(--color-primary)', margin: 0, width: '14px', height: '14px' }} />
                              {c.label}
                            </label>
                            {c.custom && isSuperAdmin && (
                              <button onClick={() => handleDeleteCustomField(c.customFieldId, c.key)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '11px', padding: '0 2px', lineHeight: 1 }} title="Remove field">✕</button>
                            )}
                          </div>
                        ))}
                        {isSuperAdmin && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '5px' }}>Custom Fields</div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <input
                                type="text"
                                value={newFieldName}
                                onChange={e => setNewFieldName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddCustomField()}
                                placeholder="Field name…"
                                style={{ fontSize: '12px', padding: '3px 7px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none', width: '120px' }}
                              />
                              <button onClick={handleAddCustomField} disabled={addingField || !newFieldName.trim()} style={{ fontSize: '11px', padding: '3px 9px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: !newFieldName.trim() ? 0.5 : 1 }}>
                                {addingField ? '…' : '+ Add'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <button onClick={() => setShowCalendar(v => !v)} className={`btn ${showCalendar ? 'btn-primary' : 'btn-secondary'}`}>📅 Calendar</button>
                <button onClick={downloadCSV} className="btn btn-secondary">↓ CSV</button>
                <button onClick={() => { resetForm(); setShowForm(true); }} className="btn btn-primary">+ Add Lead</button>
              </div>
            )}
          </div>

          {/* Tabs */}
          {!showForm && (
            <div style={{ display: 'flex', gap: '2px', marginBottom: '10px', borderBottom: '2px solid var(--color-gray-200)' }}>
              <button
                onClick={() => setShowDuplicates(false)}
                style={{ padding: '6px 14px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: !showDuplicates ? 700 : 400, color: !showDuplicates ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: !showDuplicates ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: '13px' }}
              >
                All Leads
              </button>
              <button
                onClick={() => setShowDuplicates(true)}
                style={{ padding: '6px 14px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: showDuplicates ? 700 : 400, color: showDuplicates ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: showDuplicates ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                Duplicate Phone Numbers
                {phoneDuplicateGroups.length > 0 && (
                  <span style={{ background: 'var(--color-primary)', color: '#fff', borderRadius: '2px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                    {phoneDuplicateGroups.length}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Duplicate Phone Numbers Tab */}
          {!showForm && showDuplicates && (
            <div style={{ marginBottom: '16px' }}>
              {phoneDuplicateGroups.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">✓</div>
                  <div className="empty-state-text">No duplicate phone numbers found</div>
                </div>
              ) : (
                <>
                  <div className="text-secondary mb-md" style={{ fontSize: '13px' }}>
                    {phoneDuplicateGroups.length} phone number{phoneDuplicateGroups.length !== 1 ? 's' : ''} shared by more than one lead (spaces and country code ignored).
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {phoneDuplicateGroups.map((group, gi) => (
                      <div key={gi} className="card" style={{ borderLeft: '4px solid var(--color-primary)', padding: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', backgroundColor: 'rgba(37,99,235,0.06)', borderBottom: '1px solid var(--color-gray-200)' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text)' }}>{group[0].phone || '—'}</span>
                          <span className="text-secondary" style={{ fontSize: '11px' }}>{group.length} leads</span>
                        </div>
                        <div style={{ overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                                <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Name</th>
                                <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>WhatsApp</th>
                                <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Status</th>
                                <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Created</th>
                                <th style={{ padding: '6px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.map((l, li) => (
                                <tr key={l.id} style={{ borderTop: '1px solid var(--color-gray-100)', backgroundColor: li === 0 ? 'transparent' : 'rgba(245,158,11,0.05)' }}>
                                  <td style={{ padding: '8px 14px', fontWeight: 600 }}>{l.name}</td>
                                  <td style={{ padding: '8px 14px', color: 'var(--color-secondary)' }}>{l.phone || '—'}</td>
                                  <td style={{ padding: '8px 14px' }}><span style={getStatusStyle(l.status)}>{l.status}</span></td>
                                  <td style={{ padding: '8px 14px', color: 'var(--color-secondary)' }}>{fmtDate(l.createdAt)}</td>
                                  <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                                    <button onClick={() => startEdit(l)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', marginRight: '6px' }}>Edit</button>
                                    <button onClick={() => handleDelete(l.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#DC2626' }}>Delete</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Status Filter */}
          {!showForm && !showDuplicates && (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => { setFilter('everything'); setFilterStatus(''); }} className={`btn btn-sm ${filter === 'everything' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                All ({leads.length})
              </button>
              <button onClick={() => { setFilter('all'); setFilterStatus(''); }} className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                All Open ({leads.filter(l => !ARCHIVED_STATUSES.has(l.status) && l.status !== 'ready').length})
              </button>
              {[...STATUS_OPTIONS.filter(s => !ARCHIVED_STATUSES.has(s.value) && s.value !== 'ready')].sort((a, b) => {
                const FILTER_ORDER = ['new','contacted','shows_interest','high_interest','call_not_picking','followup_later'];
                return (FILTER_ORDER.indexOf(a.value) ?? 99) - (FILTER_ORDER.indexOf(b.value) ?? 99);
              }).map(s => {
                const count = leads.filter(l => l.status === s.value).length;
                if (count === 0) return null;
                return (
                  <button key={s.value} onClick={() => { setFilter(s.value); setFilterStatus(''); }} className={`btn btn-sm ${filter === s.value ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                    {s.label} ({count})
                  </button>
                );
              })}
              <span style={{ width: '1px', height: '16px', background: 'var(--color-border)', margin: '0 2px', display: 'inline-block' }} />
              <button onClick={() => { setFilter('ready'); setFilterStatus(''); }} className={`btn btn-sm ${filter === 'ready' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                Ready ({leads.filter(l => l.status === 'ready').length})
              </button>
              <button onClick={() => { setFilter('archived'); setFilterStatus(''); }} className={`btn btn-sm ${filter === 'archived' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                Archived ({leads.filter(l => ARCHIVED_STATUSES.has(l.status)).length})
              </button>
            </div>
          )}

          {/* Search & Filters */}
          {!showForm && !showDuplicates && (
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
              <SearchInput
                placeholder="Search..."
                value={search}
                onChange={setSearch}
                style={{ width: '160px', flexShrink: 0 }}
                inputStyle={{ fontSize: '12px', padding: '4px 8px' }}
              />
              {[
                { value: filterStatus, set: setFilterStatus, opts: [{ value: '', label: 'Status' }, ...(filter === 'archived' ? STATUS_OPTIONS.filter(s => ARCHIVED_STATUSES.has(s.value)) : STATUS_OPTIONS.filter(s => !ARCHIVED_STATUSES.has(s.value))).map(s => ({ value: s.value, label: s.label }))] },
                { value: filterPath, set: setFilterPath, opts: [{ value: '', label: 'Path' }, ...PATH_OPTIONS] },
                { value: filterField, set: setFilterField, opts: [{ value: '', label: 'Field' }, ...FIELD_OPTIONS] },
                { value: filterType, set: setFilterType, opts: [{ value: '', label: 'Type' }, ...TYPE_OPTIONS.filter(o => o.value)] },
                { value: filterMode, set: setFilterMode, opts: [{ value: '', label: 'Mode' }, ...MODE_OPTIONS.filter(o => o.value)] },
                { value: filterCreator, set: setFilterCreator, opts: [{ value: '', label: 'Creator' }, ...creatorOptions] },
                { value: filterTag, set: setFilterTag, opts: [{ value: '', label: 'Tag' }, ...allTags.map(t => ({ value: String(t.id), label: t.name, color: t.color }))] },
                { value: sortBy, set: setSortBy, opts: [{ value: 'default', label: 'Sort' }, { value: 'created_newest', label: 'Created - Newest' }, { value: 'created_oldest', label: 'Created - Oldest' }, { value: 'followup_oldest', label: 'Next Follow-up' }, { value: 'updated_newest', label: 'Last Updated - Newest' }, { value: 'updated_oldest', label: 'Last Updated - Oldest' }] },
              ].map((f, i) => (
                <Select key={i} value={f.value} onChange={f.set} options={f.opts}
                  style={{ width: 'auto', flexShrink: 0 }}
                  buttonStyle={{ fontSize: '12px', padding: '4px 8px', background: f.value && i < 7 ? '#EEF2FF' : undefined, color: f.value && i < 7 ? '#4338CA' : undefined }} />
              ))}
              {hasActiveFilters && (
                <button onClick={clearFilters} style={{ fontSize: '11px', padding: '2px 7px', height: '26px', background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--color-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>✕</button>
              )}
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginLeft: '4px', whiteSpace: 'nowrap' }}>
                {filtered.length} {filtered.length === 1 ? 'lead' : 'leads'}
              </span>
            </div>
          )}

          {/* Create/Edit Form */}
          {showForm && (
            <div className="card mb-md" style={{ padding: 'var(--spacing-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-sm)', gap: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{editingId ? 'Edit Lead' : 'New Lead'}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Name *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ fontSize: '12px', padding: '4px 8px', borderColor: !form.name.trim() ? '#DC2626' : undefined }} placeholder="Contact name" />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>WhatsApp</label>
                  <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={{ fontSize: '12px', padding: '4px 8px' }} placeholder="+91 98765 43210" />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ fontSize: '12px', padding: '4px 8px' }} placeholder="email@example.com" />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>DOB</label>
                  <input className="form-input" type="date" value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} style={{ fontSize: '12px', padding: '4px 8px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Follow-up Date &amp; Time</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input className="form-input" type="date" value={form.followUpDate} onChange={e => setForm(f => ({ ...f, followUpDate: e.target.value, followUpDone: false }))}
                      style={{ fontSize: '12px', padding: '4px 8px', flex: 1 }} />
                    <input className="form-input" type="time" value={form.followUpTime} onChange={e => setForm(f => ({ ...f, followUpTime: e.target.value }))}
                      style={{ fontSize: '12px', padding: '4px 8px', width: '90px' }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Location</label>
                  <input className="form-input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} style={{ fontSize: '12px', padding: '4px 8px' }} placeholder="Berlin, Mumbai..." />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Instagram</label>
                  <input className="form-input" value={form.instagram} onChange={e => setForm(f => ({ ...f, instagram: e.target.value }))} style={{ fontSize: '12px', padding: '4px 8px' }} placeholder="@username" />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</label>
                  <Select value={form.status} onChange={v => {
                    setForm(f => ({ ...f, status: v }));
                  }} options={STATUS_OPTIONS} buttonStyle={{ fontSize: '12px', padding: '4px 8px' }} />
                </div>
                <div style={{ position: 'relative' }}>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Path</label>
                  {isSuperAdmin && (
                    <button type="button" onClick={() => { setManagingPathOptions(v => !v); setNewPathOptionName(''); }}
                      style={{ position: 'absolute', top: 0, right: 0, fontSize: '10px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {managingPathOptions ? 'Done' : 'Manage'}
                    </button>
                  )}
                  {managingPathOptions ? (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '8px', background: 'var(--color-background)' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: pathOptions.length ? '6px' : 0 }}>
                        {pathOptions.map(p => (
                          <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#EDE9FE', color: '#5B21B6', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                            {p}
                            <button type="button" onClick={() => handleRemovePathOption(p)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B21B6', padding: 0, lineHeight: 1, fontWeight: 700, fontSize: '12px', marginLeft: '2px' }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input className="form-input" value={newPathOptionName} onChange={e => setNewPathOptionName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddPathOption()}
                          placeholder="New path name" style={{ flex: 1, margin: 0, fontSize: '12px', padding: '4px 6px' }} />
                        <button type="button" onClick={handleAddPathOption} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '12px' }}>Add</button>
                      </div>
                    </div>
                  ) : (
                    <Select value={form.path} onChange={v => setForm(f => ({ ...f, path: v }))} options={[{ value: '', label: '— Path —' }, ...pathOptions.map(p => ({ value: p, label: p }))]} buttonStyle={{ fontSize: '12px', padding: '4px 8px' }} />
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Field</label>
                  {isSuperAdmin && (
                    <button type="button" onClick={() => { setManagingFieldOptions(v => !v); setNewFieldOptionName(''); }}
                      style={{ position: 'absolute', top: 0, right: 0, fontSize: '10px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {managingFieldOptions ? 'Done' : 'Manage'}
                    </button>
                  )}
                  {managingFieldOptions ? (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '8px', background: 'var(--color-background)' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: fieldOptions.length ? '6px' : 0 }}>
                        {fieldOptions.map(f => (
                          <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#DBEAFE', color: '#1E40AF', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                            {f}
                            <button type="button" onClick={() => handleRemoveFieldOption(f)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF', padding: 0, lineHeight: 1, fontWeight: 700, fontSize: '12px', marginLeft: '2px' }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input className="form-input" value={newFieldOptionName} onChange={e => setNewFieldOptionName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddFieldOption()}
                          placeholder="New field name" style={{ flex: 1, margin: 0, fontSize: '12px', padding: '4px 6px' }} />
                        <button type="button" onClick={handleAddFieldOption} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '12px' }}>Add</button>
                      </div>
                    </div>
                  ) : (
                    <Select value={form.field} onChange={v => setForm(f => ({ ...f, field: v, ...(v === 'Nursing' && !f.path ? { path: 'Work visa' } : {}) }))} options={[{ value: '', label: '— Field —' }, ...fieldOptions.map(f => ({ value: f, label: f }))]} buttonStyle={{ fontSize: '12px', padding: '4px 8px' }} />
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</label>
                  <Select value={form.customerType} onChange={v => setForm(f => ({ ...f, customerType: v }))} options={[{ value: '', label: '— Type —' }, { value: 'paying', label: 'Paying' }, { value: 'free_scheme', label: 'Free Scheme' }]} buttonStyle={{ fontSize: '12px', padding: '4px 8px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Mode</label>
                  <Select value={form.mode} onChange={v => setForm(f => ({ ...f, mode: v }))} options={[{ value: '', label: '— Mode —' }, { value: 'online', label: 'Online' }, { value: 'office', label: 'Office' }]} buttonStyle={{ fontSize: '12px', padding: '4px 8px' }} />
                </div>
              </div>
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</label>
                <textarea className="form-input" style={{ minHeight: '40px', resize: 'none', whiteSpace: 'pre-wrap', overflow: 'hidden', fontSize: '12px', padding: '4px 8px' }} value={form.notes} onChange={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; setForm(f => ({ ...f, notes: e.target.value })); }} ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} placeholder="Additional details..." />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Card</span>
                {[['', 'None', '#E2E8F0', '#475569'], ['yellow', 'Yellow', '#FEF08A', '#92400E'], ['red', 'Red', '#FCA5A5', '#991B1B']].map(([val, label, bg, color]) => (
                  <button key={val} type="button" onClick={() => setForm(f => ({ ...f, cardStatus: val }))}
                    style={{ padding: '2px 10px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: form.cardStatus === val ? bg : '#F1F5F9', color: form.cardStatus === val ? color : '#94A3B8',
                      outline: form.cardStatus === val ? `2px solid ${bg === '#F1F5F9' ? '#94A3B8' : color}` : 'none',
                    }}>{label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>RNR</span>
                {[1, 2, 3, 4, 5, 6, 7].map(n => (
                  <button key={n} type="button" onClick={() => setForm(f => ({ ...f, attemptCount: f.attemptCount === n ? null : n, ...(n === 7 && f.attemptCount !== 7 ? { status: 'no_response' } : {}) }))}
                    style={{ padding: '2px 10px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: form.attemptCount === n ? '#0891B2' : '#E2E8F0', color: form.attemptCount === n ? '#fff' : '#475569' }}>
                    {n}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="checkbox" id="leadFromEnabled" checked={!!form.leadFrom}
                    onChange={e => setForm(f => ({ ...f, leadFrom: e.target.checked ? 'suganya' : '' }))}
                    style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#6366F1', flexShrink: 0 }} />
                  <label htmlFor="leadFromEnabled" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Lead from</label>
                  {form.leadFrom && (
                    <select value={form.leadFrom} onChange={e => setForm(f => ({ ...f, leadFrom: e.target.value }))}
                      style={{ fontSize: '12px', padding: '3px 6px', border: '1px solid var(--color-border)', borderRadius: '6px', background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer', width: '110px' }}>
                      <option value="suganya">Suganya</option>
                      <option value="saravanan">Saravanan</option>
                    </select>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="checkbox" id="escalatedToEnabled" checked={!!form.escalatedTo}
                    onChange={e => setForm(f => ({ ...f, escalatedTo: e.target.checked ? 'suganya' : '' }))}
                    style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#6366F1', flexShrink: 0 }} />
                  <label htmlFor="escalatedToEnabled" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Escalated to</label>
                  {form.escalatedTo && (
                    <select value={form.escalatedTo} onChange={e => setForm(f => ({ ...f, escalatedTo: e.target.value }))}
                      style={{ fontSize: '12px', padding: '3px 6px', border: '1px solid var(--color-border)', borderRadius: '6px', background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer', width: '110px' }}>
                      <option value="suganya">Suganya</option>
                      <option value="saravanan">Saravanan</option>
                    </select>
                  )}
                </div>
              </div>
              <div style={{ marginTop: '8px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tags</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '4px', marginBottom: '4px' }}>
                  {allTags.map(t => {
                    const selected = form.tagIds.includes(t.id);
                    if (editingTag?.id === t.id) return (
                      <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <input value={editingTag.name} onChange={e => setEditingTag(et => ({ ...et, name: e.target.value }))}
                          className="form-input" style={{ fontSize: '11px', padding: '1px 5px', width: '80px' }} />
                        <input type="color" value={editingTag.color} onChange={e => setEditingTag(et => ({ ...et, color: e.target.value }))}
                          style={{ width: '20px', height: '20px', padding: 0, border: 'none', cursor: 'pointer', borderRadius: '3px' }} />
                        <button type="button" onClick={async () => { try { const res = await tagService.update(t.id, editingTag.name, editingTag.color); setAllTags(ts => ts.map(x => x.id === t.id ? res.data : x)); } catch {} setEditingTag(null); }}
                          className="btn btn-primary btn-sm" style={{ fontSize: '10px', padding: '1px 6px' }}>✓</button>
                        <button type="button" onClick={() => setEditingTag(null)} className="btn btn-secondary btn-sm" style={{ fontSize: '10px', padding: '1px 6px' }}>✕</button>
                        <button type="button" onClick={async () => {
                          if (!window.confirm(`Delete tag "${t.name}"? It will be removed from all leads.`)) return;
                          try { await tagService.delete(t.id); setAllTags(ts => ts.filter(x => x.id !== t.id)); setForm(f => ({ ...f, tagIds: f.tagIds.filter(id => id !== t.id) })); } catch {}
                          setEditingTag(null);
                        }} className="btn btn-sm" style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(220,38,38,0.1)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.2)' }}>🗑</button>
                      </span>
                    );
                    return (
                      <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                        {selected ? (
                          <button type="button" onClick={() => setForm(f => ({ ...f, tagIds: f.tagIds.filter(id => id !== t.id) }))}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: t.color, color: '#fff', border: 'none', cursor: 'pointer' }}>
                            {t.name}
                          </button>
                        ) : (
                          <button type="button" onClick={() => setForm(f => ({ ...f, tagIds: [...f.tagIds, t.id] }))}
                            style={{ padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, border: `1.5px solid ${t.color}`, cursor: 'pointer', background: 'transparent', color: t.color }}>
                            {t.name}
                          </button>
                        )}
                        <button type="button" onClick={() => setEditingTag({ id: t.id, name: t.name, color: t.color })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', color: 'var(--color-text-secondary)', padding: '0 1px', opacity: 0.6 }}>✎</button>
                      </span>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input value={newTagName} onChange={e => setNewTagName(e.target.value)} placeholder="New tag..." className="form-input" style={{ fontSize: '11px', padding: '2px 6px', width: '100px' }} />
                  <input type="color" value={newTagColor} onChange={e => setNewTagColor(e.target.value)} style={{ width: '24px', height: '24px', padding: 0, border: 'none', cursor: 'pointer', borderRadius: '4px' }} />
                  <button type="button" onClick={async () => { if (!newTagName.trim()) return; try { const res = await tagService.create(newTagName.trim(), newTagColor); setAllTags(t => [...t, res.data]); setForm(f => ({ ...f, tagIds: [...f.tagIds, res.data.id] })); setNewTagName(''); } catch {} }}
                    className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }}>+ Add</button>
                </div>
              </div>
              {editingId && <VoiceRecorder leadId={editingId} />}
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', gap: '6px' }}>
                <button onClick={handleSave} disabled={!form.name.trim() || saving} className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '3px 10px' }}>{saving ? 'Saving...' : editingId ? 'Save' : 'Create'}</button>
                <button onClick={resetForm} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '3px 10px' }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Calendar View */}
          {!showForm && !showDuplicates && showCalendar && (() => {
            const year = calendarMonth.getFullYear();
            const month = calendarMonth.getMonth();
            const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const startOffset = (firstDay + 6) % 7; // shift so Mon=0
            const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
            const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            // Build follow-up map: dateStr -> leads[]
            const fuMap = {};
            leads.forEach(l => {
              if (!l.followUpDate) return;
              if (!fuMap[l.followUpDate]) fuMap[l.followUpDate] = [];
              fuMap[l.followUpDate].push(l);
            });
            const pad = n => String(n).padStart(2, '0');
            const dayLeads = calendarDay ? (fuMap[calendarDay] || []) : [];
            return (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', maxWidth: '560px' }}>
                  {/* Month nav */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <button onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b', padding: '2px 8px' }}>‹</button>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>{monthNames[month]} {year}</span>
                    <button onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b', padding: '2px 8px' }}>›</button>
                  </div>
                  {/* Weekday headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
                    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                      <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 700, color: '#94a3b8', padding: '2px 0', textTransform: 'uppercase' }}>{d}</div>
                    ))}
                  </div>
                  {/* Day cells */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                    {Array.from({ length: totalCells }).map((_, i) => {
                      const dayNum = i - startOffset + 1;
                      if (dayNum < 1 || dayNum > daysInMonth) return <div key={i} />;
                      const dateStr = `${year}-${pad(month + 1)}-${pad(dayNum)}`;
                      const dayFu = fuMap[dateStr] || [];
                      const isSelected = calendarDay === dateStr;
                      const isToday = dateStr === new Date().toISOString().slice(0, 10);
                      const hasOverdue = dayFu.some(l => !l.followUpDone && new Date(dateStr) < new Date(new Date().toISOString().slice(0,10)));
                      return (
                        <div key={i} onClick={() => setCalendarDay(isSelected ? null : dateStr)}
                          style={{ textAlign: 'center', padding: '4px 2px', borderRadius: '6px', cursor: dayFu.length ? 'pointer' : 'default',
                            background: isSelected ? '#2563EB' : isToday ? '#EFF6FF' : 'transparent',
                            border: isToday && !isSelected ? '1px solid #BFDBFE' : '1px solid transparent' }}>
                          <div style={{ fontSize: '12px', fontWeight: isToday ? 700 : 400, color: isSelected ? '#fff' : isToday ? '#2563EB' : '#334155' }}>{dayNum}</div>
                          {dayFu.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginTop: '2px', flexWrap: 'wrap' }}>
                              {dayFu.length <= 3 ? dayFu.map((l, j) => (
                                <span key={j} style={{ width: '6px', height: '6px', borderRadius: '50%', background: isSelected ? '#fff' : hasOverdue ? '#DC2626' : '#0891B2', display: 'inline-block' }} />
                              )) : (
                                <span style={{ fontSize: '9px', fontWeight: 700, color: isSelected ? '#fff' : hasOverdue ? '#DC2626' : '#0891B2' }}>{dayFu.length}</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Day Plan Panel */}
                {calendarDay && (
                  <div style={{ marginTop: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px', maxWidth: '560px' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', marginBottom: '10px' }}>
                      {new Date(calendarDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{dayLeads.length} follow-up{dayLeads.length !== 1 ? 's' : ''}</span>
                    </div>
                    {dayLeads.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>No follow-ups scheduled</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {dayLeads.sort((a, b) => (a.followUpTime || '99:99').localeCompare(b.followUpTime || '99:99')).map(l => {
                          const timeStr = l.followUpTime ? (() => { const [h, m] = l.followUpTime.split(':'); const hr = +h; return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; })() : null;
                          const statusOpt = STATUS_OPTIONS.find(s => s.value === l.status);
                          return (
                            <div key={l.id} onClick={() => startEdit(l)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: l.followUpDone ? '#F8FAFC' : '#F0F9FF', borderRadius: '8px', cursor: 'pointer', border: '1px solid', borderColor: l.followUpDone ? '#E2E8F0' : '#BAE6FD', opacity: l.followUpDone ? 0.6 : 1 }}>
                              <div style={{ minWidth: '44px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#0891B2' }}>{timeStr || '—'}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: l.followUpDone ? 'line-through' : 'none' }}>{l.name}</div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>{l.phone || ''}</div>
                              </div>
                              {statusOpt && <span style={{ fontSize: '10px', fontWeight: 600, background: statusOpt.bg, color: statusOpt.color, borderRadius: '6px', padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 }}>{statusOpt.label}</span>}
                              {l.followUpDone && <span style={{ fontSize: '11px', color: '#059669' }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Lead List */}
          {!showForm && !showDuplicates && (
            filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🎯</div>
                <div className="empty-state-text">{filter === 'all' || filter === 'everything' ? 'No leads yet' : `No ${filter} leads`}</div>
                {(filter === 'all' || filter === 'everything') && <button onClick={() => { resetForm(); setShowForm(true); }} className="btn btn-primary mt-md">Add First Lead</button>}
              </div>
            ) : (
              <div className="card" style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                      <th style={{ padding: 'var(--spacing-sm)', width: '28px', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }} title="Drag to reorder">⠿</th>
                      {ALL_COLUMNS.filter(c => isColVisible(c.key)).map(c => (
                        <th key={c.key} style={{ padding: 'var(--spacing-sm)', textAlign: ['customerType','mode','path','field','status'].includes(c.key) ? 'center' : 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap', ...(c.width ? { width: c.width, maxWidth: c.width } : {}) }}>
                          {c.label}
                        </th>
                      ))}
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l, idx) => (
                      <tr key={l.id} onClick={() => startEdit(l)} draggable onDragStart={e => handleDragStart(e, idx)} onDragEnd={handleDragEnd} onDragOver={e => handleDragOver(e, idx)} onDrop={e => handleDrop(e, idx)} style={{ borderBottom: '1px solid var(--color-gray-100)', boxShadow: dragOverIdx === idx ? 'inset 0 -2px 0 #2563EB' : 'none', cursor: draggedIdx !== null ? 'grabbing' : 'pointer', opacity: draggedIdx === idx ? 0.15 : 1, background: dragOverIdx === idx ? 'rgba(37,99,235,0.12)' : undefined, transition: 'box-shadow 0.1s' }}>
                        <td style={{ padding: 'var(--spacing-sm)', color: 'var(--color-gray-400)', fontSize: '16px', userSelect: 'none', cursor: 'grab', width: '28px' }}>⠿</td>
                        {isColVisible('name') && <td style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.name} onClick={e => { e.stopPropagation(); setDetailLead(l); }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: 'var(--color-text-primary)' }}>
                            {l.cardStatus === 'yellow' && <span title="Suspicious" style={{ display: 'inline-block', width: '10px', height: '14px', background: '#EAB308', borderRadius: '2px', flexShrink: 0 }} />}
                            {l.cardStatus === 'red' && <span title="Blacklisted" style={{ display: 'inline-block', width: '10px', height: '14px', background: '#DC2626', borderRadius: '2px', flexShrink: 0 }} />}
                            {l.name}
                            {l.attemptCount && (
                              <span title={`RNR Attempt ${l.attemptCount}`} style={{ fontSize: '10px', fontWeight: 700, background: l.attemptCount >= 3 ? '#FEF2F2' : '#ECFEFF', color: l.attemptCount >= 3 ? '#DC2626' : '#0891B2', border: `1px solid ${l.attemptCount >= 3 ? '#FECACA' : '#A5F3FC'}`, borderRadius: '10px', padding: '0px 5px', flexShrink: 0 }}>RNR {l.attemptCount}</span>
                            )}
                          </span>
                          {(l.tags || []).length > 0 && (
                            <span title={(l.tags || []).map(t => t.name).join(', ')} style={{ marginLeft: '5px' }}>
                              {(l.tags || [])
                                .slice()
                                .sort((a, b) => allTags.findIndex(t => t.id === a.id) - allTags.findIndex(t => t.id === b.id))
                                .map(t => (
                                  <span key={t.id} style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: t.color, marginLeft: '2px', verticalAlign: 'middle' }} />
                                ))}
                            </span>
                          )}
                        </td>}
                        {isColVisible('phone') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.phone || '-'}</td>}
                        {isColVisible('followUpDate') && (() => {
  const overdue = !l.followUpDone && l.followUpDate && !['won', 'lost', 'not_interested'].includes(l.status) && (() => {
    const now = new Date();
    const dateStr = l.followUpDate + (l.followUpTime ? `T${l.followUpTime}` : 'T23:59:59');
    return new Date(dateStr) <= now;
  })();
  const isEditing = editingFollowUp?.id === l.id;
  return <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
    {isEditing ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <input
          type="date"
          ref={el => { if (el && !el.dataset.pickerOpened) { el.dataset.pickerOpened = '1'; el.focus(); try { el.showPicker(); } catch {} } }}
          value={editingFollowUp.date}
          onChange={e => setEditingFollowUp(f => ({ ...f, date: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') saveFollowUp(l.id, editingFollowUp.date, editingFollowUp.time); if (e.key === 'Escape') setEditingFollowUp(null); }}
          style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid var(--color-primary)', borderRadius: '4px', outline: 'none' }}
        />
        {editingFollowUp.time ? (() => {
          const { h, m, ampm } = parseTime12(editingFollowUp.time);
          const sel = { fontSize: '11px', padding: '2px 3px', border: '1px solid var(--color-primary)', borderRadius: '4px', outline: 'none', background: '#fff', cursor: 'pointer' };
          return <>
            <select value={h} onChange={e => setEditingFollowUp(f => ({ ...f, time: toTime24(e.target.value, m, ampm) }))} style={{ ...sel, width: '42px' }}>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={String(n)}>{n}</option>)}
            </select>
            <span style={{ fontSize: '11px', fontWeight: 600 }}>:</span>
            <select value={m} onChange={e => setEditingFollowUp(f => ({ ...f, time: toTime24(h, e.target.value, ampm) }))} style={{ ...sel, width: '42px' }}>
              {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(min => <option key={min} value={min}>{min}</option>)}
            </select>
            <button onClick={() => setEditingFollowUp(f => ({ ...f, time: toTime24(h, m, ampm === 'AM' ? 'PM' : 'AM') }))} style={{ ...sel, padding: '2px 5px', fontWeight: 700, color: ampm === 'AM' ? '#0369A1' : '#B45309' }}>{ampm}</button>
            <button onClick={() => setEditingFollowUp(f => ({ ...f, time: '' }))} title="Clear time" style={{ border: 'none', borderRadius: '4px', padding: '2px 5px', background: '#F1F5F9', color: '#94A3B8', cursor: 'pointer', fontSize: '11px' }}>✕</button>
          </>;
        })() : (
          <button onClick={() => setEditingFollowUp(f => ({ ...f, time: '09:00' }))} style={{ border: 'none', borderRadius: '4px', padding: '2px 6px', background: '#F1F5F9', color: '#64748B', cursor: 'pointer', fontSize: '11px' }}>+ time</button>
        )}
        <button onClick={() => saveFollowUp(l.id, editingFollowUp.date, editingFollowUp.time)} style={{ border: 'none', borderRadius: '4px', padding: '2px 6px', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontSize: '11px' }}>✓</button>
        <button onClick={() => setEditingFollowUp(null)} style={{ border: 'none', borderRadius: '4px', padding: '2px 6px', background: 'var(--color-gray-200)', color: '#555', cursor: 'pointer', fontSize: '11px' }}>✕</button>
      </div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span
          onClick={() => setEditingFollowUp({ id: l.id, date: l.followUpDate || '', time: l.followUpTime || '' })}
          style={{ cursor: 'pointer', color: l.followUpDone ? 'var(--color-text-secondary)' : overdue ? '#DC2626' : l.followUpDate ? 'var(--color-secondary)' : 'var(--color-text-secondary)', textDecoration: l.followUpDone ? 'line-through' : 'none', opacity: l.followUpDone ? 0.5 : 1 }}
          title="Click to set follow-up date"
        >
          {l.followUpDate
            ? <>{fmtDate(l.followUpDate)}{l.followUpTime && <span style={{ marginLeft: '4px', opacity: 0.8 }}>{(() => { const [h, m] = l.followUpTime.split(':'); const hr = +h; return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; })()}</span>}</>
            : <span style={{ opacity: 0.35 }}>—</span>
          }
        </span>
        {l.followUpDate && (
          <button
            title={l.followUpDone ? 'Mark undone' : 'Mark done'}
            onClick={() => handleFieldSave(l.id, 'followUpDone', !l.followUpDone)}
            style={{ border: 'none', borderRadius: '4px', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px', flexShrink: 0, background: l.followUpDone ? '#D1FAE5' : '#F1F5F9', color: l.followUpDone ? '#065F46' : '#94A3B8' }}
          >✓</button>
        )}
      </div>
    )}
  </td>;
})()}
                        {isColVisible('createdBy') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.creator ? cap(l.creator.loginId) : '-'}</td>}
                        {isColVisible('createdAt') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.createdAt ? (() => { const d = new Date(l.createdAt); const offset = d.getTimezoneOffset(); const tz = offset === -330 ? 'IST' : offset === -60 ? 'CET' : offset === -120 ? 'CEST' : 'GMT' + (offset <= 0 ? '+' : '') + (-offset / 60); return fmtDate(d) + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) + ' ' + tz; })() : '-'}</td>}
                        {isColVisible('location') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', width: '80px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.location || ''}>{l.location || '-'}</td>}
                        {isColVisible('status') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}><InlineBadgeSelect value={l.status || 'new'} options={STATUS_OPTIONS} onSave={v => handleFieldSave(l.id, 'status', v)} /></td>}
                        {isColVisible('age') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }} title={fmtDate(l.dateOfBirth)}>{l.dateOfBirth ? getAge(l.dateOfBirth) : '-'}</td>}
                        {isColVisible('customerType') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}><InlineBadgeSelect value={l.customerType || ''} options={TYPE_OPTIONS} onSave={v => handleFieldSave(l.id, 'customerType', v)} /></td>}
                        {isColVisible('mode') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}><InlineBadgeSelect value={l.mode || ''} options={MODE_OPTIONS} onSave={v => handleFieldSave(l.id, 'mode', v)} /></td>}
                        {isColVisible('path') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}><InlineBadgeSelect value={l.path || ''} options={PATH_OPTIONS} onSave={v => handleFieldSave(l.id, 'path', v)} /></td>}
                        {isColVisible('field') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}><InlineBadgeSelect value={l.field || ''} options={FIELD_OPTIONS} onSave={v => handleFieldSave(l.id, 'field', v)} /></td>}
                        {isColVisible('notes') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.notes || ''}>{l.notes || '-'}</td>}
                        {isColVisible('updatedAt') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.updatedAt ? (() => { const d = new Date(l.updatedAt); const offset = d.getTimezoneOffset(); const tz = offset === -330 ? 'IST' : offset === -60 ? 'CET' : offset === -120 ? 'CEST' : 'GMT' + (offset <= 0 ? '+' : '') + (-offset / 60); return fmtDate(d) + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) + ' ' + tz; })() : '-'}</td>}
                        {isColVisible('email') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.email || ''}>{l.email || '-'}</td>}
                        {isColVisible('instagram') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.instagram || '-'}</td>}
                        {isColVisible('leadFrom') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.leadFrom ? cap(l.leadFrom) : '-'}</td>}
                        {isColVisible('escalatedTo') && <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{l.escalatedTo ? cap(l.escalatedTo) : '-'}</td>}
                        {customFields.filter(f => isColVisible(f.key)).map(f => {
                          const isEditingCell = editingCustomCell?.leadId === l.id && editingCustomCell?.key === f.key;
                          const val = (l.customData || {})[f.key] || '';
                          return (
                            <td key={f.key} style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap', maxWidth: '160px' }} onClick={e => e.stopPropagation()}>
                              {isEditingCell ? (
                                <input
                                  autoFocus
                                  value={editingCustomCell.value}
                                  onChange={e => setEditingCustomCell(c => ({ ...c, value: e.target.value }))}
                                  onBlur={() => saveCustomCell(l.id, f.key, editingCustomCell.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveCustomCell(l.id, f.key, editingCustomCell.value); if (e.key === 'Escape') setEditingCustomCell(null); }}
                                  style={{ fontSize: '11px', padding: '2px 5px', border: '1px solid var(--color-primary)', borderRadius: '4px', outline: 'none', width: '120px' }}
                                />
                              ) : (
                                <span
                                  onClick={() => setEditingCustomCell({ leadId: l.id, key: f.key, value: val })}
                                  style={{ cursor: 'pointer', color: val ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '150px' }}
                                  title={val || 'Click to edit'}
                                >{val || <span style={{ opacity: 0.35 }}>—</span>}</span>
                              )}
                            </td>
                          );
                        })}
                        <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
{!l.customerId && l.status !== 'lost' && (
                              <button onClick={() => handleGenerateInvoice(l)} className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>Invoice</button>
                            )}
                            {l.customerId && (
                              <span onClick={() => handleUnconvert(l)} style={{ fontSize: '11px', color: '#059669', fontWeight: 600, padding: '2px 8px', display: 'flex', alignItems: 'center', cursor: 'pointer' }} title="Click to revert conversion">Converted ✕</span>
                            )}
                            <button onClick={() => handleDelete(l.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#DC2626' }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      {detailLead && (
        <LeadDetailModal
          lead={detailLead}
          onClose={() => setDetailLead(null)}
          onStatusSave={(id, status) => handleFieldSave(id, 'status', status)}
          onEdit={startEdit}
        />
      )}
      <Footer />
    </>
  );
};

export default Leads;
